import React from "react";
import Table from "../../common/components/Table";
import Checkbox from "../../common/components/Checkbox";
import { formatDate } from "../../../utilities/format";
import { ArrowDownTrayIcon, TrashIcon } from "@heroicons/react/24/outline";

export interface Document {
  documentId: string;
  title: string;
  fileName?: string;
  contentType: string;
  creator: string;
  createdAt: string;
  updatedAt: string;
}

interface DocumentTableProps {
  documents: Document[];
  selectedIds: string[];
  onSelectAll: (selected: boolean) => void;
  onSelectDocument: (documentId: string, selected: boolean) => void;
  onDocumentClick?: (document: Document) => void;
  onDownload?: (documentId: string) => void;
  onDelete?: (documentId: string) => void;
  isLoading?: boolean;
}

export default function DocumentTable({
  documents,
  selectedIds,
  onSelectAll,
  onSelectDocument,
  onDocumentClick,
  onDownload,
  onDelete,
  isLoading,
}: DocumentTableProps) {
  const isAllSelected = documents.length > 0 && selectedIds.length === documents.length;

  return (
    <div className="relative h-full flex flex-col">
      <Table.Wrapper className="flex-1">
        <div className="flex w-full items-center rounded-t-lg border border-black bg-black py-2">
          <div className="flex items-center justify-center px-2 text-sm font-bold text-white" style={{ width: '63px', minWidth: '63px' }}>
            <Checkbox
              isChecked={isAllSelected}
              onCheck={onSelectAll}
            />
          </div>
          <div
            className="flex items-center justify-center px-2 text-sm font-bold text-white flex-1"
            style={{ minWidth: '300px' }}
          >
            제목
          </div>
          <div
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: '120px', minWidth: '120px' }}
          >
            파일형식
          </div>
          <div
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: '180px', minWidth: '180px' }}
          >
            생성자
          </div>
          <div
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: '105px', minWidth: '105px' }}
          >
            생성일자
          </div>
          <div
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: '105px', minWidth: '105px' }}
          >
            수정일자
          </div>
          <div
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: '80px', minWidth: '80px' }}
          >
            다운로드
          </div>
          <div
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: '80px', minWidth: '80px' }}
          >
            삭제
          </div>
        </div>

        <Table.Body isLoading={isLoading} isEmpty={!documents?.length}>
          {documents?.map((document) => {
            const isSelected = selectedIds.includes(document.documentId);

            return (
              <div
                key={document.documentId}
                className="relative cursor-pointer hover:bg-gray-50"
                data-document-id={document.documentId}
                onClick={() => onDocumentClick?.(document)}
              >
                <Table.Row>
                  <div
                    className="flex items-center justify-center border-e px-2"
                    style={{ width: '63px', minWidth: '63px' }}
                  >
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        isChecked={isSelected}
                        onCheck={(checked: boolean) => onSelectDocument(document.documentId, checked)}
                      />
                    </div>
                  </div>

                  <div
                    className="flex items-center justify-start border-e px-2 flex-1"
                    style={{ minWidth: '300px' }}
                  >
                    <span className="truncate" title={document.title}>
                      {document.title}
                    </span>
                  </div>

                  <div
                    className="flex items-center justify-center border-e px-2 text-sm"
                    style={{ width: '120px', minWidth: '120px' }}
                  >
                    {document.contentType}
                  </div>

                  <div
                    className="flex items-center justify-center border-e px-2 text-sm"
                    style={{ width: '180px', minWidth: '180px' }}
                  >
                    <span className="truncate" title={document.creator}>
                      {document.creator}
                    </span>
                  </div>

                  <div
                    className="flex items-center justify-center border-e px-2 text-sm"
                    style={{ width: '105px', minWidth: '105px' }}
                  >
                    {formatDate(document.createdAt, "YY.MM.DD")}
                  </div>

                  <div
                    className="flex items-center justify-center border-e px-2 text-sm"
                    style={{ width: '105px', minWidth: '105px' }}
                  >
                    {formatDate(document.updatedAt, "YY.MM.DD")}
                  </div>

                  <div
                    className="flex items-center justify-center border-e px-2"
                    style={{ width: '80px', minWidth: '80px' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => onDownload?.(document.documentId)}
                      className="p-2 hover:bg-gray-200 rounded transition-colors"
                      title="다운로드"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5 text-blue-600" />
                    </button>
                  </div>

                  <div
                    className="flex items-center justify-center px-2"
                    style={{ width: '80px', minWidth: '80px' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => onDelete?.(document.documentId)}
                      className="p-2 hover:bg-gray-200 rounded transition-colors"
                      title="삭제"
                    >
                      <TrashIcon className="h-5 w-5 text-red-600" />
                    </button>
                  </div>
                </Table.Row>
              </div>
            );
          })}
        </Table.Body>
      </Table.Wrapper>
    </div>
  );
}